/**
 * STDB-Run-Store + StdbActions (W3, docs/09 §7):
 *  - Subscription auf runState (winzige Tabelle: 1 Zeile pro Identity)
 *  - Optimistische Vorschau: gleiche pure fn lokal + Reducer-Call
 *  - Drift-Waechter: Server-stateJson vs. serializeRun(lokal) — Server gewinnt
 *  - Zweiter Tab hat keine optimistische Kopie -> Row-Update -> Rerender (DoD 2)
 */
import type { FocusId, RunState, ShopPath } from '../game/types'
import type { HexPos } from '../sim/hex'
import type { MergeOption } from '../game/merge'
import type { RerollKind, RunActions } from '../game/actions'
import { deserializeRun, serializeRun } from '../game/persistence'
import { continueAfterResult, pickFocus, startFight, type FightOutput } from '../game/runState'
import { buySlot, choosePath, reroll, takeBonusChamp, toggleLock } from '../game/shop'
import { repairFracture } from '../game/fractures'
import {
  equipWeapon, moveToBench, placeChampion, sellChampion, sellWeapon, unequipWeapon,
} from '../game/roster'
import { executeMerge } from '../game/merge'
import { tables, type DbConnection } from './module_bindings'
import { connectStdb, type StdbSession, type StdbStatus } from './stdbConnection'

interface RunRow {
  owner: { toHexString(): string }
  stateJson: string
  status: string
}

/** Zeilen-Inhalt in das UI-Live-Objekt uebernehmen (Key-Order = Server-JSON). */
function replaceInPlace(target: RunState, source: RunState): void {
  for (const k of Object.keys(target)) delete (target as unknown as Record<string, unknown>)[k]
  Object.assign(target, source)
}

export class StdbRunStore {
  private conn: DbConnection | null = null
  private identityHex = ''
  private myRow: RunRow | null = null
  private live: RunState | null = null
  private onExternalChange: (() => void) | null = null
  /** erwartete Server-Echos der eigenen optimistischen Aktionen (BATTLE-23) */
  private pendingEchos: string[] = []
  /** Store-Level-Waiter — ueberleben Reconnects (BATTLE-23) */
  private waiters: Array<() => void> = []
  /** Run existiert serverseitig nicht mehr (abandon/anderer Tab) — UI soll ins Menue */
  onRunGone: (() => void) | null = null
  online = false

  constructor(
    private readonly uri: string,
    private readonly dbName: string,
    private readonly onStatus: (s: StdbStatus) => void,
  ) {}

  /** Erstverbindung + erste Subscription; loest nach onApplied auf. */
  async connect(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      let settled = false
      const fail = (err: unknown): void => {
        if (!settled) {
          settled = true
          reject(err instanceof Error ? err : new Error(String(err)))
        }
      }
      // BATTLE-23: ohne Timeout haengt der Boot fuer immer, wenn die
      // Subscription nie applied wird — Local-Fallback in main.ts braucht ein Reject
      const timer = setTimeout(() => fail(new Error('stdb connect: Timeout (15s)')), 15000)
      connectStdb({
        uri: this.uri,
        dbName: this.dbName,
        onStatus: s => {
          this.online = s.online
          this.onStatus(s)
        },
        onSession: (session: StdbSession) => {
          this.adoptSession(session, () => {
            if (!settled) {
              settled = true
              clearTimeout(timer)
              resolve()
            }
          }, fail)
        },
      }).catch(fail)
    })
  }

  private adoptSession(session: StdbSession, onFirstApplied: () => void, onFirstError: (err: Error) => void): void {
    this.conn = session.conn
    this.identityHex = session.identity.toHexString()
    session.conn.db.runState.onInsert((_ctx: unknown, row: RunRow) => this.onRow(row))
    session.conn.db.runState.onUpdate((_ctx: unknown, _old: RunRow, row: RunRow) => this.onRow(row))
    session.conn.db.runState.onDelete((_ctx: unknown, row: RunRow) => {
      if (row.owner.toHexString() !== this.identityHex) return
      this.myRow = null
      if (this.live) this.onRunGone?.()
    })
    session.conn
      .subscriptionBuilder()
      .onApplied(() => {
        // BATTLE-23: Der Cache der NEUEN Connection ist die Wahrheit — eine
        // waehrend der Offline-Phase geloeschte Zeile liefert KEIN Delete-Event.
        let found: RunRow | null = null
        for (const row of session.conn.db.runState.iter() as Iterable<RunRow>) {
          if (row.owner.toHexString() === this.identityHex) found = row
        }
        this.myRow = found
        if (!found && this.live) {
          console.warn('[stdb] Run existiert auf dem Server nicht mehr — beende lokale Sicht')
          this.onRunGone?.()
        } else {
          this.recheckDrift()
        }
        this.notifyWaiters()
        onFirstApplied()
      })
      .onError((ctx: unknown) => {
        console.error('[stdb] Subscription fehlgeschlagen', ctx)
        onFirstError(new Error('stdb: Subscription fehlgeschlagen'))
      })
      .subscribe(tables.runState)
  }

  private onRow(row: RunRow): void {
    if (row.owner.toHexString() !== this.identityHex) return
    this.myRow = row
    this.recheckDrift()
    this.notifyWaiters()
  }

  private notifyWaiters(): void {
    for (const w of [...this.waiters]) w()
  }

  /** erwartetes Echo einer eigenen optimistischen Aktion vormerken (BATTLE-23) */
  notePending(stateJson: string): void {
    this.pendingEchos.push(stateJson)
    if (this.pendingEchos.length > 32) this.pendingEchos.shift()
  }

  private recheckDrift(): void {
    if (!this.myRow || !this.live) return
    // Echo-Queue: pipelinede Aktionen (Klick B innerhalb der RTT von Klick A)
    // duerfen keinen False-Positive-Drift ausloesen — Zwischenstaende matchen
    const echoIdx = this.pendingEchos.indexOf(this.myRow.stateJson)
    if (echoIdx >= 0) {
      this.pendingEchos.splice(0, echoIdx + 1)
      return
    }
    const expected = serializeRun(this.live)
    if (this.myRow.stateJson === expected) {
      this.pendingEchos = []
      return
    }
    const server = deserializeRun(this.myRow.stateJson)
    if (!server) {
      console.warn('[stdb] Server-State nicht lesbar — ignoriert (Version?)')
      return
    }
    // Drift: fremder Tab ODER echte Vorschau-Abweichung — Server gewinnt (docs/09 §7)
    console.warn('[stdb] State-Drift erkannt — uebernehme Server-Autoritaet')
    this.pendingEchos = []
    replaceInPlace(this.live, server)
    this.onExternalChange?.()
  }

  /** Server-Stand erzwingen — Heilpfad wenn ein Reducer serverseitig ablehnt (BATTLE-23) */
  forceServerSync(): void {
    if (!this.live) return
    this.pendingEchos = []
    if (this.myRow) {
      const server = deserializeRun(this.myRow.stateJson)
      if (server) {
        replaceInPlace(this.live, server)
        this.onExternalChange?.()
      }
    } else {
      this.onRunGone?.()
    }
  }

  /** aktuellen Server-Run lesen (Menu "Fortsetzen") */
  current(): RunState | null {
    return this.myRow && this.myRow.status === 'active'
      ? deserializeRun(this.myRow.stateJson)
      : null
  }

  /** Neuen Run anlegen und auf die Server-Zeile warten.
   *  BATTLE-23: Waiter lebt am STORE (nicht an der Connection) — ueberlebt Reconnects. */
  createRun(seed: number): Promise<RunState> {
    const conn = this.requireConn()
    return new Promise<RunState>((resolve, reject) => {
      const cleanup = (): void => {
        clearTimeout(t)
        this.waiters = this.waiters.filter(w => w !== check)
      }
      const t = setTimeout(() => {
        cleanup()
        reject(new Error('create_run: Timeout'))
      }, 10000)
      const check = (): void => {
        const state = this.current()
        if (state && state.masterSeed === (seed >>> 0)) {
          cleanup()
          resolve(state)
        }
      }
      this.waiters.push(check)
      const p = conn.reducers.createRun({ seed: seed >>> 0 }) as unknown
      if (p && typeof (p as Promise<unknown>).catch === 'function') {
        ;(p as Promise<unknown>).catch((err: unknown) => {
          cleanup()
          reject(err instanceof Error ? err : new Error(String(err)))
        })
      }
      check()
    })
  }

  /** Live-UI-State binden: Drift-Korrekturen + Fremd-Tab-Updates -> rerender. */
  attach(state: RunState, onExternalChange: () => void): () => void {
    this.live = state
    this.onExternalChange = onExternalChange
    this.recheckDrift()
    return () => {
      if (this.live === state) {
        this.live = null
        this.onExternalChange = null
      }
    }
  }

  actionsFor(state: RunState): RunActions {
    return new StdbActions(state, this)
  }

  requireConn(): DbConnection {
    if (!this.conn) throw new Error('nicht verbunden')
    return this.conn
  }
}

/** Optimistische Vorschau + Reducer-Dispatch (Objekt-Syntax, docs/09 §8). */
class StdbActions implements RunActions {
  constructor(
    private readonly state: RunState,
    private readonly store: StdbRunStore,
  ) {}

  /** Reducer feuern und Fehlschlag beobachten — Rejection heisst Tx-Rollback
   *  OHNE Row-Event, der Drift-Waechter allein kann das nie heilen (BATTLE-23). */
  private fire(call: (conn: DbConnection) => unknown): void {
    const p = call(this.store.requireConn())
    if (p && typeof (p as Promise<unknown>).catch === 'function') {
      ;(p as Promise<unknown>).catch((err: unknown) => {
        console.error('[stdb] Reducer abgelehnt — erzwinge Server-Sync', err)
        this.store.forceServerSync()
      })
    }
  }

  private guard(ok: boolean, call: (conn: DbConnection) => unknown): boolean {
    if (!this.store.online) return false
    if (ok) {
      this.store.notePending(serializeRun(this.state))
      this.fire(call)
    }
    return ok
  }

  choosePath(path: ShopPath): boolean {
    return this.guard(
      this.store.online && choosePath(this.state, path),
      c => c.reducers.shopChoosePath({ path }),
    )
  }
  reroll(kind: RerollKind): boolean {
    return this.guard(
      this.store.online && reroll(this.state, kind),
      c => c.reducers.shopReroll({ kind }),
    )
  }
  toggleLock(slot: number): boolean {
    return this.guard(
      this.store.online && toggleLock(this.state, slot),
      c => c.reducers.shopLock({ slot }),
    )
  }
  buySlot(slot: number): boolean {
    return this.guard(
      this.store.online && buySlot(this.state, slot),
      c => c.reducers.shopBuy({ slot }),
    )
  }
  takeBonusChamp(): boolean {
    return this.guard(
      this.store.online && takeBonusChamp(this.state),
      c => c.reducers.shopTakeBonus({}),
    )
  }
  pickFocus(focus: FocusId): boolean {
    return this.guard(
      this.store.online && pickFocus(this.state, focus),
      c => c.reducers.shopPickFocus({ focus }),
    )
  }
  repairFracture(): boolean {
    return this.guard(
      this.store.online && repairFracture(this.state),
      c => c.reducers.repairFracture({}),
    )
  }
  placeChampion(uid: number, hex: HexPos): boolean {
    return this.guard(
      this.store.online && placeChampion(this.state, uid, hex),
      c => c.reducers.rosterPlace({ uid, col: hex.col, row: hex.row }),
    )
  }
  moveToBench(uid: number): boolean {
    return this.guard(
      this.store.online && moveToBench(this.state, uid),
      c => c.reducers.rosterBench({ uid }),
    )
  }
  equipWeapon(champUid: number, weaponUid: number): boolean {
    return this.guard(
      this.store.online && equipWeapon(this.state, champUid, weaponUid),
      c => c.reducers.rosterEquip({ champUid, weaponUid }),
    )
  }
  unequipWeapon(champUid: number, weaponUid: number): boolean {
    return this.guard(
      this.store.online && unequipWeapon(this.state, champUid, weaponUid),
      c => c.reducers.rosterUnequip({ champUid, weaponUid }),
    )
  }
  sellChampion(uid: number): boolean {
    return this.guard(
      this.store.online && sellChampion(this.state, uid),
      c => c.reducers.rosterSellChampion({ uid }),
    )
  }
  sellWeapon(uid: number): boolean {
    return this.guard(
      this.store.online && sellWeapon(this.state, uid),
      c => c.reducers.rosterSellWeapon({ uid }),
    )
  }
  executeMerge(option: MergeOption): boolean {
    return this.guard(
      this.store.online && executeMerge(this.state, option),
      c => c.reducers.rosterMerge({ kind: option.kind, key: option.key, tier: option.tier }),
    )
  }
  startFight(): FightOutput | null {
    if (!this.store.online) return null
    const out = startFight(this.state)
    if (out) {
      this.store.notePending(serializeRun(this.state))
      this.fire(c => c.reducers.shopConfirm({}))
    }
    return out
  }
  continueAfterResult(): void {
    if (!this.store.online || this.state.phase !== 'result') return
    continueAfterResult(this.state)
    this.store.notePending(serializeRun(this.state))
    this.fire(c => c.reducers.continueAfterResult({}))
  }
  persist(): void {
    // Server persistiert — bewusst leer (docs/09 §6)
  }
  abandonRun(): void {
    if (this.store.online) this.fire(c => c.reducers.abandonRun({}))
  }
}
